import './windowConfig';
import 'jquery/dist/jquery.min';
import '@/assets/styles/tailwind.css';
import Vue from 'vue';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import '@mdi/font/css/materialdesignicons.css';
import Vuetify from 'vuetify';
import FpServices from '@/services/FpServices';
import {FpAxios} from '@/services/FpAxios';
import App from './App.vue';
import router from './router';
import store from './store/store';
import EnvironmentService from '@/env/EnvironmentService';
import AuthService from '@/modules/user/AuthService';
import moment from 'moment';
import 'moment/locale/pl';
import 'moment/locale/ro';
import {Utils} from '@/services/utils/BasicUtils';
import pl from 'vuetify/src/locale/pl';
import en from 'vuetify/src/locale/en';
import ro from 'vuetify/src/locale/ro';
import uk from 'vuetify/src/locale/uk';
import de from 'vuetify/src/locale/de';

import GoToService from './router/GoToService';
import CommonComponents from '@/components/commons/CommonComponents';
import {AxiosError, AxiosResponse} from 'axios';
import UserCom from '@/services/log/UserCom';
import TagService from '@/services/log/TagService';
import Logger from '@/services/log/Logger';
import {TokenType, TOKEN_TYPE_KEY} from '@/commons/enums/TokenType';
import ClientAuthService from '@/modules/client/services/ClientAuthService';
import BadgesPlugin from '@/components/commons/Badges';
import {
  I18NGetter
} from '@/services/enumTranslator/I18NGetter';
import EnvironmentTheme from '@/env/services/EnvironmentTheme';
import {VUETIFY_BREAKPOINT_CONFIG} from '@/commons/constants/breakpoints';
import {bindVuetifyBreakpoint} from '@/composables/useBreakpoints';
import { PiniaVuePlugin } from 'pinia';
import pinia from '@/store/pinia';
import {VuetifyLocale} from 'vuetify/types/services/lang';
import {I18N} from '@/services/enumTranslator/I18NDictionary';
import {Onboarding} from '@/plugins/onboarding';
import {PlatformNotifications} from '@/plugins/platformNotifications';
import {SystemLanguage} from '@/services/enumTranslator/SystemLanguage';
import {i18nFormatter} from '@/services/StringFormatter';
import {isRefValid} from '@/services/utils/isRefValid';
// @ts-ignore
import VHtmlSafe from '@/directives/commons/VHtmlSafe';
import ModalService from '@/components/fpComponents/services/ModalService/ModalService';
import { installBrowserZoomGuard } from '@/services/browserZoomGuard';
import SnackbarService from '@/components/fpComponents/SnackbarService';
import processProxy from '@/env/processProxy';
import InstantSearch from 'vue-instantsearch';
import { hasAccessToNewDealFlow } from './modules/user/services/Utils';
import { setupClientPanelPosthogTracking } from './plugins/posthog';
import { usePreloadedIframe } from '@/composables/usePreloadedIframe';
import {polyfillCountryFlagEmojis} from 'country-flag-emoji-polyfill';

polyfillCountryFlagEmojis(
  'Twemoji Country Flags',
  `${process.env.BASE_URL}fonts/TwemojiCountryFlags.woff2`,
);

moment.locale(I18N.systemLanguage.toLowerCase());

TagService.connect();
UserCom.connect();
Logger.connect();

Vue.config.productionTip = false;
Vue.prototype.$env = EnvironmentService.Environment;
Vue.prototype.$utils = Utils;
Vue.prototype.$goto = GoToService;
Vue.prototype.$validate = function(key: string, scrollContainer?: HTMLElement, validateFormValue: boolean = true): boolean {
  const ref: any = this.$refs[key];
  if (ref && ref.$el && !scrollContainer) {
    const closestContainer = ref.$el.closest('.v-card__text');
    if (closestContainer) {
      scrollContainer = closestContainer as HTMLElement | undefined;
    }
  }

  return isRefValid(ref, key, scrollContainer, !!validateFormValue);
};

Object.defineProperty(Vue.prototype, '$user', {
  get() {
    return AuthService.User;
  },
});

Object.defineProperty(Vue.prototype, '$i18nFormatter', {
  get() {
    return i18nFormatter;
  },
});

Object.defineProperty(Vue.prototype, '$hasAccessToNewDealFlow', {
  get() {
    return hasAccessToNewDealFlow(AuthService.User);
  },
});

FpAxios.BaseUrl = processProxy.VUE_APP_BASE_URL;
FpAxios.tokenGetter = () => AuthService.token;

FpAxios.onNotFound = (error: AxiosError) => Logger.endpointErrorHandler(error);
FpAxios.onBadRequest = (error: AxiosError) => Logger.endpointErrorHandler(error);

const getBearerTokenFromRequestConfig = (error: AxiosError): string | null => {
  const headers = error.config?.headers as Record<string, unknown> | undefined;
  const authorizationHeader = headers?.authorization || headers?.Authorization;

  if (typeof authorizationHeader !== 'string') {
    return null;
  }

  return authorizationHeader.replace(/^Bearer\s+/i, '').trim();
};

const updateAuthHeaderAndRetry = (
  token: string,
  requestConfig: AxiosError['config'],
  axiosInstance: any
) => {
  if (!requestConfig) {
    return null;
  }

  requestConfig.headers = requestConfig.headers || {};
  (requestConfig.headers as Record<string, string>).authorization = `Bearer ${token}`;

  return axiosInstance(requestConfig);
};

/** Nested 401 during Zitadel recovery (e.g. /info from UserLoaded while renew is in flight) must not start another renew loop. */
let zitadel401RecoveryDepth = 0;

const isAuthInfoAxiosRequest = (config: AxiosError['config']): boolean => {
  const url = config?.url || '';
  return url.includes('auth/info');
};

FpAxios.onUnauthorized = async (error: AxiosError, axiosInstance) => {
  Logger.endpointErrorHandler(error);
  if (localStorage.getItem(TOKEN_TYPE_KEY) === TokenType.CLIENT) {
    ClientAuthService.logout();
  } else {
    const isZitadel = AuthService.isZitadelAuthMode;
    // Attempt token renewal for Zitadel mode
    if (isZitadel && error.config && !(error.config as any)._isRetry) {
      (error.config as any)._isRetry = true;
      if (zitadel401RecoveryDepth > 0) {
        AuthService.softLogout(router.currentRoute);
        return;
      }
      zitadel401RecoveryDepth++;
      try {
        const failedToken = getBearerTokenFromRequestConfig(error);
        /** JWT still valid but API returned 401 — OIDC silent renew will not fix backend/session rejection; avoids mfa/login ↔ callback loops. */
        if (
          isAuthInfoAxiosRequest(error.config)
          && failedToken
          && !AuthService.isJwtAccessTokenExpired(failedToken)
        ) {
          AuthService.softLogout(router.currentRoute);
          return;
        }

        const latestAccessToken = AuthService.accessToken;
        if (latestAccessToken && latestAccessToken !== failedToken) {
          return updateAuthHeaderAndRetry(latestAccessToken, error.config, axiosInstance);
        }

        // Wait for the OIDC built-in automaticSilentRenew if it is already running.
        if (AuthService.isAutomaticRenewInProgress) {
          const autoRenewedUser = await AuthService.waitForAutomaticRenew();
          const autoRenewedToken = autoRenewedUser?.access_token || AuthService.accessToken;
          if (autoRenewedToken && autoRenewedToken !== failedToken) {
            return updateAuthHeaderAndRetry(autoRenewedToken, error.config, axiosInstance);
          }
        }

        const tokenAfterAutomaticRenewWait = AuthService.accessToken;
        if (tokenAfterAutomaticRenewWait && tokenAfterAutomaticRenewWait !== failedToken) {
          return updateAuthHeaderAndRetry(tokenAfterAutomaticRenewWait, error.config, axiosInstance);
        }

        const renewedUser = await AuthService.renewToken();
        if (renewedUser?.access_token && renewedUser.access_token !== failedToken) {
          return updateAuthHeaderAndRetry(renewedUser.access_token, error.config, axiosInstance);
        }

        const tokenAfterRenewAttempt = AuthService.accessToken;
        if (tokenAfterRenewAttempt && tokenAfterRenewAttempt !== failedToken) {
          return updateAuthHeaderAndRetry(tokenAfterRenewAttempt, error.config, axiosInstance);
        }
      } catch (e) {
        Logger.error('Error during token renewal in 401 handler', { error: e });
      } finally {
        zitadel401RecoveryDepth--;
      }
    }
    AuthService.softLogout(router.currentRoute);
  }
};

Object.defineProperty(Vue.prototype, '$i18n', {
  get() {
    return I18NGetter();
  },
});

FpAxios.onForbidden = (error: AxiosError) => {
  Logger.endpointErrorHandler(error);
  if (localStorage.getItem(TOKEN_TYPE_KEY) === TokenType.CLIENT) {
    ClientAuthService.logout();
  } else if (error.response?.data.key === 'forbidden_hr_locked') {
    Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useMain.ERROR_HR_LOCKED);
    GoToService.hrLocked();
  } else {
    Vue.prototype.$snackbarService.openErrorSnackbar(I18NGetter().useMain.ERROR_NO_ACCESS);
    GoToService.index();
  }
};

FpAxios.revokeToken = (r: AxiosResponse) => AuthService.revokeToken(r);

FpAxios.defaultHeaders = {};
FpAxios.defaultHeaders[processProxy.VUE_APP_HEADER_NAME_COMMIT_HASH!] = processProxy.VUE_APP_COMMIT_HASH!;
Vue.use(BadgesPlugin);
Vue.use(PiniaVuePlugin);
Vue.use(Vuetify);
Vue.use(FpServices);
Vue.use(Onboarding);
Vue.use(PlatformNotifications);
Vue.use(CommonComponents);
Vue.use(VHtmlSafe);
Vue.prototype.$modalService = ModalService;
Vue.prototype.$snackbarService = SnackbarService;
Vue.use(InstantSearch);
setupClientPanelPosthogTracking(router);


const vuetifyLocales: Record<Lowercase<SystemLanguage>, VuetifyLocale> = {
  pl,
  en,
  ro,
  uk,
  de,
};

const vuetify = new Vuetify({
  breakpoint: VUETIFY_BREAKPOINT_CONFIG,
  theme: {
    options: {
      customProperties: true,
      variations: false,
    },
    dark: false,
    themes: EnvironmentTheme.LENDI,
  },
  lang: {
    locales: vuetifyLocales,
    current: I18N.systemLanguage.toLowerCase(),
  },
});
// Keep Vuetify Breakpoint service (needs init()), but read flags from our Tailwind singleton.
bindVuetifyBreakpoint(vuetify.framework.breakpoint);
Vue.prototype.$vuetify = vuetify.framework;

// Register the persistent preloaded-iframe listener before the app mounts so
// READY is never missed.
usePreloadedIframe().setup();

// Vuetify blokuje zoom przeglądarki (ctrl+kółko / pinch) pod każdym otwartym v-dialog —
// szczegóły w module. Jedna rejestracja na cały czas życia aplikacji.
installBrowserZoomGuard();

function removeStartupSplash() {
  document.getElementById('splash-screen')?.remove();
}

new Vue({
  router,
  store,
  // @ts-ignore
  vuetify,
  pinia,
  render: h => h(App),
}).$mount('#app');

router.onReady(() => {
  requestAnimationFrame(() => {
    removeStartupSplash();
  });
});
